import styled from "styled-components";
import { rawColors } from "../../../design-system/tokens/colors";
import { spacing } from "../../../design-system/tokens/spacing";
import {
  borderWidth,
  borderStyle,
  borderRadius as radius,
} from "../../../design-system/tokens/borders";
import {
  fontWeights,
  typographyMixin,
} from "../../../design-system/tokens/typography";
import {
  KG_ONTOLOGY_NODE_SIZE_PX,
  KG_NODE_LABEL_TEXT_GAP_PX,
} from "../../kg/kgNodeDimensions";
import { kgCategorySelectionShadow } from "../../kg/kgLineageTokens";
import {
  kgNodeEllipsisTextCenterStyles,
  kgNodeTextContainerStyles,
} from "../../kg/kgNodeText.styles";
import type { KgNodeVisualState } from "../../kg/kgNodeShared.types";

/** Figma 2124-9927 — 112×112 circle, green stroke, tinted shadow */
export const Circle = styled.div<{
  $visualState: KgNodeVisualState;
  $isSelected: boolean;
}>`
  width: ${KG_ONTOLOGY_NODE_SIZE_PX}px;
  height: ${KG_ONTOLOGY_NODE_SIZE_PX}px;
  box-sizing: border-box;
  border-radius: 200px;
  border: ${borderWidth[1]}
    ${({ $visualState }) =>
      $visualState === "draft" ? borderStyle.dashed : borderStyle.solid}
    ${rawColors.accent.ontologyGreen};
  background: ${rawColors.structural.white};
  box-shadow: ${({ $isSelected }) =>
    kgCategorySelectionShadow("ontology", $isSelected)};
  outline: none;
  transition: box-shadow 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing[3]} ${spacing[5]};
  overflow: hidden;
  cursor: pointer;
`;

/** Icon ↔ text block — Figma outer gap 8px */
export const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing[2]};
  text-align: center;
  ${kgNodeTextContainerStyles}
`;

/** "Ontology" + domain name — Figma inner stack has no row gap (tight) */
export const TextStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${KG_NODE_LABEL_TEXT_GAP_PX}px;
  ${kgNodeTextContainerStyles}
`;

/** Figma icon container — 32×32, 8px radius, green tint */
export const IconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${radius.lg};
  background: ${rawColors.accent.ontologyGreenBg};
  color: ${rawColors.accent.ontologyGreen};
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const LabelRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${spacing[1]};
  ${kgNodeTextContainerStyles}
`;

/** Figma Overline 3 — 10px / medium / 14px line-height */
export const Label = styled.span`
  ${typographyMixin("overline", 4)}
  font-weight: ${fontWeights.medium};
  color: ${rawColors.text.mediumEmphasis};
  ${kgNodeEllipsisTextCenterStyles}
`;

/** Figma domain name — Caption 3, centered */
export const Name = styled.span`
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.highEmphasis};
  ${kgNodeEllipsisTextCenterStyles}
`;
